import { PenLine, Building2, History, ShieldCheck, Sparkles, Target, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { Overline, PageHero, CTABand } from "@/components/Bits";
import Accreditations from "@/components/Accreditations";
import { DIRECTOR_IMG, HERO_IMG } from "@/lib/site";

const VALUES = [
  { icon: ShieldCheck, label: "Integrity" },
  { icon: Award, label: "Professionalism" },
  { icon: Target, label: "Precision" },
  { icon: Sparkles, label: "Excellence" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About Us"
        title="Crafting travel that powers business."
        subtitle="A distinguished leader in air ticketing, visa processing, and hotel bookings, serving travel agents, corporates, and executives globally."
        image={HERO_IMG}
      />

      {/* Director's letter */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-24 md:pb-32">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl">
              <img src={DIRECTOR_IMG} alt="Abul Jaish Ahmad, CEO" className="w-full h-[520px] object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/60 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <p className="text-white font-heading text-2xl font-semibold">Abul Jaish Ahmad</p>
                <p className="text-white/80">CEO, Fly Gowell Travel Solutions</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="inline-flex rounded-xl bg-brand-blue/5 p-3 text-brand-blue">
              <PenLine size={24} />
            </div>
            <Overline className="mt-6 block">Message from the CEO</Overline>
            <div className="mt-6 space-y-5 text-lg text-brand-slate leading-relaxed">
              <p>
                A warm welcome to Fly Gowell Travel Solutions. Corporate travel is more than
                logistics. It is about productivity, comfort, and reliability while enabling
                business success.
              </p>
              <p>
                Our team handles every detail with precision, from complex itineraries to
                last-minute executive travel and premium solutions. We don&apos;t just plan trips.
                We craft experiences that enhance business efficiency and create lasting value.
              </p>
            </div>
            <div className="mt-8 border-t border-brand-blue/10 pt-6">
              <p className="font-heading text-xl text-brand-blue italic">Warm regards,</p>
              <p className="font-heading text-2xl font-semibold text-brand-blue mt-1">Abul Jaish Ahmad</p>
              <p className="text-brand-slate">CEO, Fly Gowell Travel Solutions</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Overview + Legacy */}
      <section className="bg-white border-y border-brand-blue/5">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32 grid gap-12 md:grid-cols-2 md:gap-16">
          <Reveal>
            <div className="inline-flex rounded-xl bg-brand-ocean/10 p-3 text-brand-ocean">
              <Building2 size={24} />
            </div>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">Company Overview</h2>
            <p className="mt-5 text-lg text-brand-slate leading-relaxed">
              Fly Gowell is a distinguished leader in the travel industry, specializing in air
              ticketing, visa processing, and hotel bookings. Headquartered in New Delhi, we serve
              travel agents, corporates, and executives globally. Our direct links with diplomatic
              missions let us fast-track visa processes and deliver seamless travel experiences.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="inline-flex rounded-xl bg-brand-green/10 p-3 text-brand-green">
              <History size={24} />
            </div>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">Legacy &amp; Rebranding</h2>
            <p className="mt-5 text-lg text-brand-slate leading-relaxed">
              Founded in 2006 by Mr. Gufran Ahmad, Fly Gowell was built on decades of industry
              knowledge. What began as Gowell Travels has evolved into Fly Gowell Travel Solutions,
              rebranded but powered by the same values, now with renewed energy and a sharper
              corporate focus.
            </p>
            <div className="mt-8 inline-flex items-baseline gap-3 rounded-2xl bg-brand-bg px-6 py-4 border border-brand-blue/8">
              <span className="font-heading text-4xl font-extrabold text-brand-blue">2006</span>
              <span className="text-brand-slate">Founded as Gowell Travels</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <Reveal><Overline>What We Stand For</Overline></Reveal>
        <RevealGroup className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v) => (
            <RevealItem key={v.label}>
              <Link to="/contact" data-testid={`value-${v.label.toLowerCase()}`} className="block rounded-2xl border border-brand-blue/8 p-6 sm:p-8 text-center hover:-translate-y-1 hover:shadow-lg transition-all bg-white cursor-pointer">
                <div className="mx-auto inline-flex rounded-xl bg-gradient-to-br from-brand-blue to-brand-green p-3.5 text-white">
                  <v.icon size={24} />
                </div>
                <p className="mt-5 font-heading text-base sm:text-lg font-semibold text-brand-blue break-words hyphens-auto">{v.label}</p>
              </Link>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      <section className="border-t border-brand-blue/5 bg-white py-16">
        <Accreditations />
      </section>

      <CTABand />
    </div>
  );
}
